import { useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useRegionLanguage } from '@common/Context/RegionLanguageContext';
import { DropdownControl } from '@common/Component/Forms';
import { COUNTRY_LIST } from '@common/Utility/constants';
import Image from 'next/image';
import { sendEventData } from '@asterCore/Auth/Analytics';
import { EVENT_NAMES, getLanguage, getPage, getRegionName, MY_ASTER_CATEGORY } from '@common/Constants/analytics';
import { getReferralPageForGA, groupByKey, isEmpty, clearAppStorage } from '@common/Utility/common-utils';
import { isRTL, useTranslation } from '@common/i18n';
import { useAuth } from '@common/AuthService';
import { RegionLanguageSwitcherStyled } from './RegionLanguageSwitcherStyled';

export default function RegionLanguageSwitcher({ screenName, pageType }) {
  const { regionLang, updateRegionLang } = useRegionLanguage();
  const router = useRouter();
  const t = useTranslation();
  const { authCtx } = useAuth();

  const selectedCaption = useMemo(
    () => COUNTRY_LIST.find((country) => country.regionName === regionLang),
    [regionLang],
  );

  const langByRegion = useMemo(
    () =>
      COUNTRY_LIST.find(
        (country) =>
          country.regionCode === selectedCaption?.regionCode && country.langCode !== selectedCaption?.langCode,
      ),
    [selectedCaption],
  );

  const filteredCountryList = useMemo(() => {
    if (!selectedCaption) return [];
    const groupedCountries = groupByKey(COUNTRY_LIST, 'regionCode');

    return Object.keys(groupedCountries)
      .filter((key) => key !== selectedCaption.regionCode)
      .map(
        (key) =>
          groupedCountries[key].find((country) => country.langCode === selectedCaption.langCode) ||
          groupedCountries[key][0],
      );
  }, [selectedCaption]);

  const handleChange = useCallback(
    (selectedRegionLang) => {
      const newSelectedRegionLang = COUNTRY_LIST.find((country) => country.regionName === selectedRegionLang);
      if (!newSelectedRegionLang || newSelectedRegionLang.regionName === regionLang) return;

      const { page } = getPage(router?.asPath);
      const referralPageForGa = getReferralPageForGA(router?.asPath) || 'Aster Core Login';

      sendEventData({
        event: EVENT_NAMES.COUNTRY_SELECTION,
        screenName: screenName || page,
        pageType: pageType || page,
        category: MY_ASTER_CATEGORY.AE,
        referrerPage: referralPageForGa,
        content_language: getLanguage(newSelectedRegionLang.langCode),
        selected_country: getRegionName(newSelectedRegionLang.regionCode),
      });
      clearAppStorage(() => {
        if (authCtx?.authenticated) authCtx?.logout();
        updateRegionLang(newSelectedRegionLang.regionName, 'region');
      });
    },
    [regionLang, updateRegionLang, router, screenName, pageType, authCtx],
  );

  const changeLanguage = (selectedLang) => {
    updateRegionLang(selectedLang, 'language');
  };

  return (
    <RegionLanguageSwitcherStyled className={isRTL(selectedCaption?.langCode) ? 'rtl' : ''}>
      <DropdownControl
        theme="regionLanguageDropdown"
        enableFullWidth
        data={filteredCountryList}
        caption={
          selectedCaption ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="ic">
                <Image src={selectedCaption.flagImage} height={24} width={32} alt="country flag" />
              </div>
              <div>{selectedCaption.region}</div>
            </div>
          ) : (
            t('Header.txtSelectRegionLanguage')
          )
        }
        data-test="country"
        handleSelect={handleChange}
      />
      {!isEmpty(langByRegion) && (
        <>
          <span className="lang-separator">|</span>
          <span className="lang-selector" onClick={() => changeLanguage(langByRegion.regionName)}>
            {langByRegion.language}
          </span>
        </>
      )}
    </RegionLanguageSwitcherStyled>
  );
}
